import { datadogRum } from '@datadog/browser-rum'
import { Ref } from 'vue'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import { NotificationType } from '@/legacy/types/notifications'

/**
 * Provides utility functions for copying and viewing Connect article and library links.
 * @param externalPatientId - Optional Ref containing the external Thymebox patient ID to include in copied links.
 * @returns {object} An object containing copyLink and viewArticle functions.
 */
export function useArticleLinks(
  externalPatientId?: Readonly<Ref<string | undefined>>
) {
  const copyLink = async (sourceUrl: string) => {
    try {
      const url = new URL(sourceUrl)
      const isLibraryLink = url.pathname === '/library'

      url.searchParams.set('utm_campaign', 'thymecare_tb')
      url.searchParams.set('utm_content', 'Library_Article')
      url.searchParams.set('utm_medium', 'sms')
      if (externalPatientId?.value) {
        url.searchParams.set('tc', externalPatientId.value)
      }

      await navigator.clipboard.writeText(url.toString())

      const actionName = isLibraryLink
        ? 'connectLibraryLinkCopied'
        : 'connectArticleLinkCopied'
      datadogRum.addAction(actionName, {
        url: url.toString(),
      })
    } catch (err) {
      useNotificationStore().setNotification({
        message: `Failed to copy link. ${err}`,
        type: NotificationType.DANGER,
      })
    }
  }

  const viewArticle = (careTeamDocumentId: string) => {
    datadogRum.addAction('connectArticleViewed', { careTeamDocumentId })
    window.open(`/tools/library/${careTeamDocumentId}`, '_blank')
  }

  return {
    copyLink,
    viewArticle,
  }
}
